import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import vacanciesService from '../services/vacancies.service';
import { VacancyCreateData, Vacancy } from '../types/VacancyTypes';
import { autoLabel } from '../utils/autoLabel';
import CompanyVerificationModal from '../components/Verification/CompanyVerificationModal';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

interface PostVacancyProps {
  darkMode: boolean;
}

const PostVacancy: React.FC<PostVacancyProps> = ({ darkMode }) => {
  const navigate = useNavigate();
  const { isAuthenticated, verificationStatus, refreshVerificationStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isVerified = verificationStatus?.is_verified ?? false;
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const [form, setForm] = useState<VacancyCreateData>({
    title: '',
    company_name: verificationStatus?.company_name || '',
    description: '',
    requirements: '',
    bounty_amount: 0,
    bounty_currency: 'USD',
    location: '',
    is_remote: false,
    employment_type: 'full-time',
  });

  // Auto-suggest tags from title
  const suggestedTags = useMemo(() => {
    if (!form.title.trim()) return null;
    const fakeVacancy = {
      ...form,
      id: '', posted_by: '', posted_by_name: '', is_verified_poster: false,
      status: 'open' as const, referral_count: 0, created_at: '', updated_at: '',
    } as Vacancy;
    return autoLabel(fakeVacancy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.description, form.is_remote]);

  // Sync company_name when verification status updates (e.g. after just verifying)
  useEffect(() => {
    if (verificationStatus?.is_verified && verificationStatus.company_name) {
      setForm(prev => ({ ...prev, company_name: verificationStatus.company_name! }));
    }
  }, [verificationStatus]);

  if (!isAuthenticated) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <div className="empty-state">
          <h3>Sign in to post a vacancy</h3>
          <p>You need to be logged in to post vacancies with referral bounties.</p>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <div className="verification-required-prompt">
          <LockOutlinedIcon sx={{ fontSize: 48, color: '#4f46e5', marginBottom: '16px' }} />
          <h2>Verification Required</h2>
          <p>You need to verify your company email before posting vacancies. This ensures trust in the referral marketplace.</p>
          <button
            className="btn-primary"
            onClick={() => setShowVerificationModal(true)}
            style={{ marginTop: 16, padding: '12px 32px', fontSize: '1rem' }}
          >
            Verify Now
          </button>
        </div>
        <CompanyVerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onSuccess={() => {
            refreshVerificationStatus();
          }}
        />
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked
        : name === 'bounty_amount' ? Number(value)
        : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.company_name || !form.description) {
      setError('Title, company name, and description are required.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const vacancy = await vacanciesService.createVacancy(form, isVerified);
      navigate(`/vacancies/${vacancy.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create vacancy.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`page-container ${darkMode ? 'dark' : ''}`}>
      <div className="form-page">
        <h1>Post a Vacancy</h1>
        <p className="form-subtitle">Add a referral bounty to attract the best candidates through your network.</p>

        {isVerified && (
          <div className="verification-notice">
            Your vacancy will display a verified badge for <strong>{verificationStatus?.company_name}</strong>.
          </div>
        )}

        {error && <div className="form-error">{error}</div>}

        <form onSubmit={handleSubmit} className="vacancy-form">
          <div className="form-group">
            <label htmlFor="title">Job Title *</label>
            <input
              id="title"
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. Senior Backend Engineer"
              required
            />
            {suggestedTags && form.title.trim() && (
              <div className="tag-suggestions">
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginRight: '4px' }}>Detected:</span>
                <span className="tag-suggestion-chip">{suggestedTags.department}</span>
                <span className="tag-suggestion-chip">{suggestedTags.seniority}</span>
                <span className="tag-suggestion-chip">{suggestedTags.workStyle}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="company_name">
              Company Name *
              {isVerified && (
                <VerifiedIcon sx={{ fontSize: 14, color: '#1976d2', marginLeft: '4px', verticalAlign: 'middle' }} />
              )}
            </label>
            <input
              id="company_name"
              name="company_name"
              type="text"
              value={form.company_name}
              onChange={handleChange}
              placeholder="e.g. Acme Corp"
              required
              disabled={isVerified}
              className={isVerified ? 'input-locked' : ''}
            />
            {isVerified && (
              <small className="input-locked-notice">Locked to your verified company</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the role, team, and what makes it exciting..."
              rows={5}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="requirements">Requirements</label>
            <textarea
              id="requirements"
              name="requirements"
              value={form.requirements}
              onChange={handleChange}
              placeholder="Skills, experience level, certifications..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bounty_amount">Referral Bounty ($)</label>
              <input
                id="bounty_amount"
                name="bounty_amount"
                type="number"
                min="0"
                value={form.bounty_amount}
                onChange={handleChange}
                placeholder="5000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bounty_currency">Currency</label>
              <select
                id="bounty_currency"
                name="bounty_currency"
                value={form.bounty_currency}
                onChange={handleChange}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                id="location"
                name="location"
                type="text"
                value={form.location}
                onChange={handleChange}
                placeholder="e.g. San Francisco, CA"
              />
            </div>

            <div className="form-group">
              <label htmlFor="employment_type">Employment Type</label>
              <select
                id="employment_type"
                name="employment_type"
                value={form.employment_type}
                onChange={handleChange}
              >
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
                <option value="internship">Internship</option>
              </select>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_remote"
                checked={form.is_remote}
                onChange={handleChange}
              />
              This position is remote-friendly
            </label>
          </div>

          <button type="submit" className="btn-primary btn-submit" disabled={loading}>
            {loading ? 'Creating...' : 'Post Vacancy'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PostVacancy;
